import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, UserPlus, Lock } from "lucide-react";
import { Character } from "@/components/story-setup/CharacterForm";
import CharacterPreview from "@/components/character/CharacterPreview";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { logger } from "@/lib/logger";

interface ExtractedCharactersProps {
  storyId: string;
  aiProvider: string;
  canExtractCharacter: boolean;
}

const ExtractedCharacters = ({ storyId, aiProvider, canExtractCharacter }: ExtractedCharactersProps) => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [extractedCharacters, setExtractedCharacters] = useState<Character[]>([]);
  const [selectedCharacters, setSelectedCharacters] = useState<Set<string>>(new Set());
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);

  const extractCharacters = async () => {
    if (!canExtractCharacter) {
      setIsUpgradeDialogOpen(true);
      return;
    }
    setIsLoading(true);
    try {
      const { data: { characters }, error } = await supabase.functions.invoke('extract-characters', {
        body: { storyId, aiProvider }
      });

      if (error) throw error;

      setExtractedCharacters(characters.map((char: any) => ({
        name: char.name,
        description: char.description,
        role: char.role || 'character',
        gender: char.gender,
        age_group: char.age_group,
        personality_traits: char.personality_traits || [],
        strengths: char.strengths || [],
        weaknesses: char.weaknesses || [],
        skills: char.skills || [],
        translation_overrides: {}
      })));

    } catch (error) {
      logger.error('Error extracting characters:', error);
      toast({
        variant: "destructive",
        title: t('errors.error'),
        description: t('errors.loadCharactersError'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCharacterSelect = (characterName: string) => {
    const newSelected = new Set(selectedCharacters);
    if (newSelected.has(characterName)) {
      newSelected.delete(characterName);
    } else {
      newSelected.add(characterName);
    }
    setSelectedCharacters(newSelected);
  };

  const saveSelectedCharacters = async () => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      toast({
        variant: "destructive",
        title: t('errors.error'),
        description: t('errors.notAuthenticated'),
      });
      return;
    }

    const selectedCharactersList = extractedCharacters.filter(char => 
      selectedCharacters.has(char.name)
    );

    try {
      const { error } = await supabase
        .from('characters')
        .insert(selectedCharactersList.map(char => ({
          ...char,
          user_id: user.id,
          story_origin_id: storyId,
          is_extracted: true
        })));

      if (error) throw error;

      toast({
        title: t('success.charactersSaved'),
        description: t('success.charactersAddedToLibrary'),
      });

      setSelectedCharacters(new Set());
    } catch (error) {
      logger.error('Error saving characters:', error);
      toast({
        variant: "destructive",
        title: t('errors.error'),
        description: t('errors.savingFailed'),
      });
    }
  };

  if (extractedCharacters.length === 0) {
    return (
      <div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant={!canExtractCharacter ? "locked" : "outline"}
                onClick={extractCharacters}
                disabled={isLoading}
                className="bg-[#7E69AB] text-white hover:bg-[#6E59A5] w-full"
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="mr-2 h-4 w-4" />
                )}
                {t('story.extractCharacters')}
                {!canExtractCharacter && (
                  <Lock className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            {!canExtractCharacter && (
              <TooltipContent>
                <p>{t('story.tooltips.upgradeToExtract')}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <Dialog open={isUpgradeDialogOpen} onOpenChange={setIsUpgradeDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('story.upgrade.title')}</DialogTitle>
              <DialogDescription>
                {t('story.upgrade.extractDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              <span>{t('story.upgrade.extractFeature')}</span>
            </div>
            <DialogFooter className="sm:justify-start">
              <Button
                type="button"
                onClick={() => {
                  setIsUpgradeDialogOpen(false);
                  navigate("/dashboard/more");
                }}
                className="w-full sm:w-auto"
              >
                {t('story.upgrade.upgradeButton')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4">
      <h3 className="text-lg font-semibold">
        {t('story.foundCharacters')}
      </h3>
      <div className="grid gap-4 md:grid-cols-2">
        {extractedCharacters.map((character) => {
          const completeCharacter = {
            ...character,
            role: character.role || 'character',
            personality_traits: character.personality_traits || [],
            strengths: character.strengths || [],
            weaknesses: character.weaknesses || [],
            skills: character.skills || [],
            translation_overrides: character.translation_overrides || {}
          };

          return (
            <Card
              key={character.name}
              className={`cursor-pointer transition-colors ${
                selectedCharacters.has(character.name)
                  ? 'ring-2 ring-primary'
                  : ''
              }`}
              onClick={() => handleCharacterSelect(character.name)}
            >
              <CharacterPreview character={completeCharacter} />
            </Card>
          );
        })}
      </div>
      {selectedCharacters.size > 0 && (
        <Button onClick={saveSelectedCharacters} className="mt-4">
          {t('story.addSelectedCharacters')} ({selectedCharacters.size})
        </Button>
      )}
    </div>
  );
};

export default ExtractedCharacters;
